import { ChangeEventHandler, useEffect } from 'react';
import {
  Grid, Paper, Stack, TextField,
} from '@mui/material';
import { v4 as uuid } from 'uuid';
import BlockProps from '../../../models/BlockProps';
import BlockHtmlData from './BlockHtmlData';
import Utils from '../../../utils/Utils';
import CloseButton from '../../Buttons/Close';
import EditableText from '../../Form/EditableText';

function BlockHtml({
  data, isEditing, isTemplate, editHandler,
}: BlockProps) {
  const props = (data.data as BlockHtmlData);

  const extractScriptURLfromHtml = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const scripts = doc.getElementsByTagName('script');
    const urls: string[] = [];
    for (let i = 0; i < scripts.length; i += 1) {
      const src = scripts[i].getAttribute('src');
      if (src) {
        urls.push(src);
      }
    }

    return urls;
  };

  const handleHtml: ChangeEventHandler<HTMLInputElement> = (event) => {
    props.html.value = event.target.value;
  };

  const handleBlur: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    editHandler(data.id, props);
    props.html.value = event.target.value;
  };

  const handleTitleUpdate = (text: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  // load scripts from html when page loads or html is updated
  useEffect(() => {
    const urls = extractScriptURLfromHtml(props.html.value);
    urls.forEach((url) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      document.body.appendChild(script);
    });
  }, [props.html.value]);

  return (
    <Stack
      spacing={0}
      sx={{
        padding: 1.5,
        overflow: 'hidden',
      }}
    >
      {(isEditing && !isTemplate) && (
        <Stack spacing={0} sx={{ border: '1px dashed lightGray', p: 2, m: 2 }}>
          <TextField
            sx={{
              '& textarea': {
                resize: 'vertical',
              },
            }}
            key={uuid()}
            label="Embedded HTML"
            multiline
            rows={4}
            defaultValue={props.html?.value}
            onChange={handleHtml}
            onBlur={handleBlur}
          />
        </Stack>
      )}

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        className="block-content-wrapper"
      >
        {props.title && (
          <Grid item sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}>
            <CloseButton
              show={isEditing}
              updateHandler={() => {
                props.title = undefined;
                editHandler(data.id, props);
              }}
            />
            <EditableText
              text={props.title.value}
              textColor={props.title.settings?.textColor}
              isEditable={isEditing && !isTemplate}
              updateHandler={handleTitleUpdate}
            />
          </Grid>
        )}
        <Grid
          container
          direction="column"
          sx={{
            justifyContent: 'center', alignItems: 'center', width: '100%', height: 'auto',
          }}
        >
          {props.html.value && (
            <div
              id={`block-html-${data.id}`}
              style={{ width: '100%' }}
              className="block-html-container notranslate"
              translate="no"
              dangerouslySetInnerHTML={{ __html: props.html.value }}
            />
          )}
          {!props.html.value && (
            <Paper
              elevation={0}
              sx={{
                width: '100%', height: 450, background: '#F4F4F6', border: '1px dashed lightGray',
              }}
            />
          )}
        </Grid>
      </Grid>
    </Stack>
  );
}

export default BlockHtml;
